import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const WorkspaceContextFormSchema = z.object({
  title: z
    .string()
    .min(2, { message: "Title must be at least 2 characters" })
    .max(250, { message: "Title must be at most 250 characters" })
    .nonempty({ message: "Title is required" }),
  context: z.string().nonempty({ message: "Context is required" }),
});

type WorkspaceContextFormValues = z.infer<typeof WorkspaceContextFormSchema>;

interface WorkspaceContextFormProps {
  onSubmit: (data: WorkspaceContextFormValues) => void;
  defaultValues?: WorkspaceContextFormValues;
}

export function WorkspaceContextForm({
  onSubmit,
  defaultValues,
}: WorkspaceContextFormProps) {
  const workspaceContextForm = useForm<WorkspaceContextFormValues>({
    resolver: zodResolver(WorkspaceContextFormSchema),
    defaultValues: defaultValues || {
      title: "",
      context: "",
    },
  });

  // Reset form when default values change (for editing mode)
  useEffect(() => {
    if (defaultValues) {
      workspaceContextForm.reset(defaultValues);
    }
  }, [defaultValues, workspaceContextForm]);

  return (
    <Form {...workspaceContextForm}>
      <form
        id="workspace-context-form"
        onSubmit={workspaceContextForm.handleSubmit(onSubmit)}
        className="space-y-8"
      >
        <FormField
          control={workspaceContextForm.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="What should this be called?" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={workspaceContextForm.control}
          name="context"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Context</FormLabel>
              <FormControl>
                <Textarea
                  className="max-h-[400px] min-h-[200px]"
                  placeholder="Provide any information you want the scenario creation AI to know."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
